import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { API } from "./utils/constants";

// Page components
import { PublicEstimateView, PublicInvoiceView, PublicChangeOrderView } from "./pages/PublicViews";
import { AuthenticationSystem } from "./pages/AuthPage";
import { UserManagementModule } from "./pages/UserManagement";
import { GearsLandingPage, ComingSoonPage } from "./pages/GearsLandingPage";
import ProductionGear from "./pages/ProductionGear";
import EngineeringGearPage from "./pages/EngineeringGearPage";
import ManagementGearPage from "./pages/ManagementGearPage";
import HumanResourcesGearPage from "./pages/HumanResourcesGearPage";
import CustomerCareGearPage from "./pages/CustomerCareGearPage";
import TrainingGearPage from "./pages/TrainingGearPage";
import SalesGearPage from "./pages/SalesGearPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import CompanySettingsPage from "./pages/CompanySettingsPage";
import OnboardingWizard from "./pages/OnboardingWizard";

const REIFY_LOGO = 'https://customer-assets.emergentagent.com/job_dbd68d1b-28e6-42d0-902a-82a09fb5a2e9/artifacts/65npxqdg_atdzxq91_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2008_54_02%20AM.png';

function ForcePasswordChange({ currentUser, onPasswordChanged, onLogout }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      onPasswordChanged({ ...currentUser, mustChangePassword: false });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-testid="force-password-change">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <img src={REIFY_LOGO} alt="Reify Systems" className="h-12 mx-auto mb-3 object-contain" />
          <p className="text-gray-500 text-sm">Welcome, {currentUser.name}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-bold text-center text-white mb-2">Set Your Password</h2>
          <p className="text-gray-500 text-sm text-center mb-6">Please change your temporary password before continuing.</p>
          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Current (Temporary) Password</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="current-password-input" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Min 8 chars, upper, lower, number, special"
                data-testid="new-password-input" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-1">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="confirm-password-input" />
            </div>
            <button type="submit" disabled={saving}
              className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-lg font-bold transition-colors mt-2"
              data-testid="change-password-submit">
              {saving ? 'Changing...' : 'Set Password & Continue'}
            </button>
          </form>
        </div>
        <div className="text-center mt-4">
          <button onClick={onLogout} className="text-gray-500 hover:text-gray-400 text-sm" data-testid="logout-link">Sign out</button>
        </div>
      </div>
    </div>
  );
}

// Setup axios interceptors for JWT token handling
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh state (shared across all interceptor invocations)
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Setup axios response interceptor with automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiry');
        window.location.reload();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentToken = localStorage.getItem('authToken');
      if (!currentToken) {
        isRefreshing = false;
        window.location.reload();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        const newToken = data.access_token;
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('tokenExpiry', Date.now() + (8 * 60 * 60 * 1000));
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiry');
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function App() {
  const [currentGear, setCurrentGear] = useState('gears');
  const [currentUser, setCurrentUser] = useState(null);

  // Check for existing authentication token on app load
  useEffect(() => {
    const checkAuthToken = async () => {
      const token = localStorage.getItem('authToken');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (token && tokenExpiry && Date.now() < parseInt(tokenExpiry)) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const userData = response.data;
          const userWithPermissions = {
            id: userData.id,
            email: userData.email,
            name: userData.full_name,
            role: userData.role === 'admin' ? 'Admin/Manager' : 'Field Staff',
            permissions: {
              canEdit: userData.role === 'admin',
              canViewFinancials: userData.role === 'admin',
              canManageUsers: userData.role === 'admin',
              canUploadPhotos: true,
              canViewJobs: true
            },
            rawRole: userData.role,
            isActive: userData.is_active,
            isLocked: userData.is_locked,
            companyId: userData.company_id || '',
            isPlatformAdmin: userData.is_platform_admin || false,
            mustChangePassword: userData.must_change_password || false,
            company: userData.company || null,
          };
          
          setCurrentUser(userWithPermissions);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('tokenExpiry');
        }
      }
    };
    
    checkAuthToken();
  }, []);
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiry');
    setCurrentUser(null);
    setCurrentGear('gears');
    // Reset to default branding on logout
    document.title = 'Reify Systems';
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) favicon.href = '/favicon.ico';
  };

  // Dynamic branding: update page title and favicon based on logged-in company
  useEffect(() => {
    if (currentUser?.company) {
      const companyName = currentUser.company.name;
      if (companyName) document.title = companyName;
      const logoUrl = currentUser.company.logo_url;
      if (logoUrl) {
        const favicon = document.querySelector("link[rel='icon']");
        if (favicon) favicon.href = logoUrl;
      }
    }
  }, [currentUser]);
  
  // Public routes
  const publicEstimateMatch = window.location.pathname.match(/^\/estimate\/(.+)$/);
  const publicInvoiceMatch = window.location.pathname.match(/^\/invoice\/(.+)$/);
  const publicChangeOrderMatch = window.location.pathname.match(/^\/change-order\/(.+)$/);

  if (publicEstimateMatch) {
    return <PublicEstimateView publicToken={publicEstimateMatch[1]} />;
  }
  if (publicChangeOrderMatch) {
    return <PublicChangeOrderView publicToken={publicChangeOrderMatch[1]} />;
  }
  if (publicInvoiceMatch) {
    return <PublicInvoiceView publicToken={publicInvoiceMatch[1]} />;
  }

  // Auth gate
  if (!currentUser) {
    return <AuthenticationSystem onLogin={setCurrentUser} />;
  }

  // Force password change on first login
  if (currentUser.mustChangePassword) {
    return <ForcePasswordChange currentUser={currentUser} onPasswordChanged={(updatedUser) => setCurrentUser(updatedUser)} onLogout={handleLogout} />;
  }

  // Onboarding wizard for new company admins
  const needsOnboarding = currentUser.rawRole === 'admin'
    && !currentUser.isPlatformAdmin
    && currentUser.company
    && currentUser.company.onboarding_completed === false;

  if (needsOnboarding) {
    return (
      <OnboardingWizard
        currentUser={currentUser}
        onComplete={() => setCurrentUser(prev => ({
          ...prev,
          company: { ...prev.company, onboarding_completed: true }
        }))}
        onLogout={handleLogout}
      />
    );
  }

  // Standalone platform admin — go directly to Platform Admin dashboard
  if (currentUser.isPlatformAdmin && !currentUser.companyId) {
    return <PlatformAdminPage currentUser={currentUser} onLogout={handleLogout} />;
  }

  const renderPage = () => {
    if (currentGear === 'company-settings' && currentUser?.rawRole === 'admin') {
      return <CompanySettingsPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }

    if (currentGear === 'user-management') {
      return <UserManagementModule onBack={() => setCurrentGear('gears')} currentUser={currentUser} />;
    }
    
    if (currentGear === 'gears') {
      return (
        <GearsLandingPage
          onNavigate={(gearId) => {
            setCurrentGear(gearId);
          }}
          currentUser={currentUser}
          onLogout={handleLogout}
        />
      );
    }

    // Module access enforcement: check company modules_enabled
    const companyModules = currentUser?.company?.modules_enabled || [];
    const moduleGears = ['production', 'management', 'human-resources', 'sales', 'customer-care', 'engineering', 'training'];
    if (moduleGears.includes(currentGear) && companyModules.length > 0 && !companyModules.includes(currentGear)) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 max-w-lg w-full text-center">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Module Not Available</h1>
            <p className="text-gray-600 text-sm mb-6">This module is not enabled for your company. Contact your platform administrator to enable it.</p>
            <button
              onClick={() => setCurrentGear('gears')}
              className="px-5 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium"
              data-testid="module-blocked-back-btn"
            >
              Back to Gears
            </button>
          </div>
        </div>
      );
    }
    
    if (currentGear === 'production') {
      return <ProductionGear currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }
    
    if (currentGear === 'engineering') {
      return <EngineeringGearPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }
    
    if (currentGear === 'management') {
      return <ManagementGearPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }
    
    // Employee gear access enforcement
    if (currentUser?.rawRole !== 'admin') {
      const allowed = Array.isArray(currentUser?.allowedGears) ? currentUser.allowedGears : ['production', 'training'];
      if (currentGear !== 'gears' && !allowed.includes(currentGear)) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800 flex items-center justify-center">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 max-w-lg w-full">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
              <p className="text-gray-600 mb-6">You do not have access to this section. Please contact an administrator.</p>
              <button
                onClick={() => setCurrentGear('gears')}
                className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="access-denied-back-btn"
              >
                Back to Gears
              </button>
            </div>
          </div>
        );
      }
    }

    if (currentGear === 'human-resources') {
      return <HumanResourcesGearPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }

    if (currentGear === 'training') {
      return <TrainingGearPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }

    if (currentGear === 'customer-care') {
      return <CustomerCareGearPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }

    if (currentGear === 'sales') {
      return <SalesGearPage currentUser={currentUser} onBackToGears={() => setCurrentGear('gears')} />;
    }

    return <ComingSoonPage gear={currentGear} onBackToGears={() => setCurrentGear('gears')} currentUser={currentUser} />;
  };

  return (
    <div className="App">
      {renderPage()}
    </div>
  );
}

export default App;
